import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { ArrowLeft, Download, File, FileText, KeyRound, Mail, Phone, Save, ShieldCheck, Upload, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { auth, db, storage } from '../../firebase/config';

type UserProfileData = {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  role?: string;
  assignedLawyerId?: string | null;
};

type ProfileFileRecord = {
  id: string;
  fileName: string;
  fileUrl: string;
  createdAt: string;
  uploadedByRole?: string;
};

type ProfileNoteEntry = {
  content: string;
  createdAt: string;
  createdByEmail: string;
  createdByUid: string;
  createdByRole: string;
};

const ProfilePage: React.FC = () => {
  const { userId } = useParams();
  const { user, assignedLawyerId, role } = useAuth();
  const profileUserId = userId || user?.uid || '';
  const isOwnProfile = !!user && profileUserId === user.uid;

  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [viewedRole, setViewedRole] = useState<string>('');
  const [notes, setNotes] = useState<ProfileNoteEntry[]>([]);
  const [newNoteDraft, setNewNoteDraft] = useState('');
  const [files, setFiles] = useState<ProfileFileRecord[]>([]);
  const [isAssignedLawyerViewer, setIsAssignedLawyerViewer] = useState(false);
  const [assignedLawyerLabel, setAssignedLawyerLabel] = useState('Not assigned');
  const [saving, setSaving] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const canManageProfileRecords = !isOwnProfile && viewedRole === 'client' && (role === 'admin' || isAssignedLawyerViewer);
  const canViewProfileRecords = !accessDenied && (viewedRole === 'client' || isOwnProfile);

  useEffect(() => {
    const loadProfile = async () => {
      if (!user || !profileUserId) return;

      setProfileLoading(true);
      setAccessDenied(false);
      setErrorMessage('');

      const userDoc = await getDoc(doc(db, 'users', profileUserId));
      if (userDoc.exists()) {
        const data = userDoc.data() as UserProfileData;
        const targetRole = data.role || '';
        const targetAssignedLawyerId = data.assignedLawyerId || null;
        const lawyerCanAccess = role === 'lawyer' && targetRole === 'client' && targetAssignedLawyerId === user.uid;
        const adminCanAccess = role === 'admin';
        const selfCanAccess = profileUserId === user.uid;

        setIsAssignedLawyerViewer(lawyerCanAccess);

        if (!selfCanAccess && !adminCanAccess && !lawyerCanAccess) {
          setAccessDenied(true);
          setIsAssignedLawyerViewer(false);
          setProfileLoading(false);
          return;
        }

        setFullName(data.fullName || '');
        setPhoneNumber(data.phoneNumber || '');
        setViewedRole(targetRole);

        const noteDoc = await getDoc(doc(db, 'profileNotes', profileUserId));
        if (noteDoc.exists()) {
          const noteData = noteDoc.data() as {entries?: ProfileNoteEntry[]; content?: string; updatedAt?: string; updatedByRole?: string};
          if (Array.isArray(noteData.entries)) {
            const sortedEntries = [...noteData.entries].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
            setNotes(sortedEntries);
          } else if (noteData.content) {
            setNotes([
              {
                content: noteData.content,
                createdAt: noteData.updatedAt || new Date().toISOString(),
                createdByEmail: 'unknown@unknown',
                createdByUid: 'unknown',
                createdByRole: noteData.updatedByRole || 'staff',
              },
            ]);
          } else {
            setNotes([]);
          }
        } else {
          setNotes([]);
        }

        const filesSnap = await getDocs(query(collection(db, 'profileFiles'), where('profileUserId', '==', profileUserId)));
        const profileFiles = filesSnap.docs
          .map((fileDoc) => ({ id: fileDoc.id, ...fileDoc.data() } as ProfileFileRecord))
          .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
        setFiles(profileFiles);

        if (targetAssignedLawyerId) {
          const lawyerDoc = await getDoc(doc(db, 'users', targetAssignedLawyerId));
          if (lawyerDoc.exists()) {
            const lawyerData = lawyerDoc.data() as UserProfileData;
            setAssignedLawyerLabel(lawyerData.fullName || lawyerData.email || 'Assigned lawyer');
          } else {
            setAssignedLawyerLabel('Assigned lawyer');
          }
        } else if (targetRole === 'lawyer') {
          setAssignedLawyerLabel('You are a lawyer account');
        } else if (targetRole === 'admin') {
          setAssignedLawyerLabel('Not applicable for admin accounts');
        } else {
          setAssignedLawyerLabel('Not assigned');
        }
      } else {
        setErrorMessage('Profile not found.');
      }

      setProfileLoading(false);
    };

    loadProfile().catch((error) => {
      console.error('Error loading profile:', error);
      setErrorMessage('Unable to load profile information right now.');
      setProfileLoading(false);
    });
  }, [assignedLawyerId, profileUserId, role, user]);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user || !isOwnProfile) return;

    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await updateDoc(doc(db, 'users', profileUserId), {
        fullName,
        phoneNumber,
      });
      setSuccessMessage('Profile updated successfully.');
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrorMessage('Unable to save your profile right now.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!canManageProfileRecords) return;

    const noteContent = newNoteDraft.trim();
    if (!noteContent || !user?.uid) return;

    setSavingNotes(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const noteEntry: ProfileNoteEntry = {
        content: noteContent,
        createdAt: new Date().toISOString(),
        createdByEmail: user.email || 'unknown@unknown',
        createdByUid: user.uid,
        createdByRole: role || 'staff',
      };

      const nextEntries = [noteEntry, ...notes];

      await setDoc(doc(db, 'profileNotes', profileUserId), {
        profileUserId,
        entries: nextEntries,
        content: noteEntry.content,
        updatedAt: new Date().toISOString(),
        updatedByEmail: user.email || null,
        updatedByUid: user.uid,
        updatedByRole: role || null,
      });
      setNotes(nextEntries);
      setNewNoteDraft('');
      setSuccessMessage('Client note added successfully.');
    } catch (error) {
      console.error('Error saving notes:', error);
      setErrorMessage('Unable to save notes right now.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user || !canManageProfileRecords) return;

    setUploadingFile(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const storageRef = ref(storage, `profile-files/${profileUserId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);

      await addDoc(collection(db, 'profileFiles'), {
        profileUserId,
        fileName: file.name,
        fileUrl,
        createdAt: new Date().toISOString(),
        uploadedByUid: user.uid,
        uploadedByRole: role,
      });

      const updatedFiles = await getDocs(query(collection(db, 'profileFiles'), where('profileUserId', '==', profileUserId)));
      const nextFiles = updatedFiles.docs
        .map((fileDoc) => ({ id: fileDoc.id, ...fileDoc.data() } as ProfileFileRecord))
        .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
      setFiles(nextFiles);
      setSuccessMessage('File uploaded successfully.');
      event.target.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrorMessage('Unable to upload the file right now.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email || !isOwnProfile) return;

    setResetMessage('');
    setErrorMessage('');

    try {
      await sendPasswordResetEmail(auth, user.email);
      setResetMessage(`Password reset email sent to ${user.email}.`);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setErrorMessage('Unable to send password reset email right now.');
    }
  };

  if (profileLoading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading profile...</div>;
  }

  if (accessDenied) {
    return (
      <div className="container" style={{ padding: '40px 0' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', backgroundColor: 'white', borderRadius: '12px', boxShadow: 'var(--shadow)', padding: '30px' }}>
          <h1 style={{ color: '#0f172a', marginBottom: '12px' }}>Profile Access Restricted</h1>
          <p style={{ color: '#666', marginBottom: '20px' }}>You do not have permission to view this profile.</p>
          <Link to={role ? `/${role}` : '/'} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            <ArrowLeft size={18} /> Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px 0' }}>
      <div style={{ maxWidth: '920px', margin: '0 auto' }}>
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ color: '#0f172a', marginBottom: '10px' }}>Profile</h1>
          <p style={{ color: '#666' }}>
            {isOwnProfile ? 'Manage your personal information, review your assigned lawyer details, and reset your password.' : 'Review this client profile, manage read-only notes, and upload reference files.'}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '24px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: 'var(--shadow)', padding: '30px' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
              <User size={22} /> Personal Details
            </h3>

            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Email Address</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', border: '1px solid #e1e1e1', borderRadius: '8px', backgroundColor: '#f9f9f9', color: '#555' }}>
                  <Mail size={18} />
                  <span>{isOwnProfile ? user?.email || 'No email available' : 'Profile email shown in account overview'}</span>
                </div>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label htmlFor="fullName" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Full Name</label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  placeholder="Enter your full name"
                  disabled={!isOwnProfile}
                  style={{ width: '100%', padding: '12px 14px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label htmlFor="phoneNumber" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <Phone size={18} style={{ position: 'absolute', top: '13px', left: '12px', color: '#777' }} />
                  <input
                    id="phoneNumber"
                    name="phoneNumber"
                    type="text"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    placeholder="Enter your phone number"
                    disabled={!isOwnProfile}
                    style={{ width: '100%', padding: '12px 14px 12px 40px', border: '1px solid #ddd', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {successMessage && <div style={{ marginBottom: '16px', color: '#2e7d32', backgroundColor: '#edf7ed', border: '1px solid #c8e6c9', borderRadius: '8px', padding: '12px 14px' }}>{successMessage}</div>}
              {errorMessage && <div style={{ marginBottom: '16px', color: '#c62828', backgroundColor: '#fff5f5', border: '1px solid #ffc1c1', borderRadius: '8px', padding: '12px 14px' }}>{errorMessage}</div>}

              {isOwnProfile ? (
                <button type="submit" className="btn btn-primary" disabled={saving} style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
                  <Save size={18} />
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              ) : (
                <div style={{ color: '#777', fontSize: '0.9rem' }}>Personal details are read-only when viewing another user profile.</div>
              )}
            </form>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: 'var(--shadow)', padding: '30px' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                <ShieldCheck size={22} /> Account Overview
              </h3>
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Role</div>
                <div style={{ fontWeight: 600 }}>{(isOwnProfile ? role : viewedRole)?.toUpperCase() || 'Unknown'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.8rem', color: '#777', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Assigned Lawyer</div>
                <div style={{ fontWeight: 600 }}>{assignedLawyerLabel}</div>
              </div>
            </div>

            {canViewProfileRecords && (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: 'var(--shadow)', padding: '30px' }}>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                  <FileText size={22} /> Client Notes
                </h3>
                {canManageProfileRecords && (
                  <>
                    <label htmlFor="profile-note-draft" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>Add Note</label>
                    <textarea
                      id="profile-note-draft"
                      name="noteDraft"
                      value={newNoteDraft}
                      onChange={(event) => setNewNoteDraft(event.target.value)}
                      rows={5}
                      placeholder="Add a new client note"
                      style={{ width: '100%', padding: '12px 14px', border: '1px solid #ddd', borderRadius: '8px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '14px', fontFamily: 'inherit' }}
                    />
                    <button type="button" className="btn btn-secondary" onClick={handleSaveNotes} disabled={savingNotes}>
                      {savingNotes ? 'Adding Note...' : 'Add Note'}
                    </button>
                  </>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: canManageProfileRecords ? '18px' : 0 }}>
                  {notes.length === 0 ? (
                    <div style={{ color: '#777', fontSize: '0.95rem' }}>No notes available.</div>
                  ) : (
                    notes.map((note, index) => (
                      <div key={`${note.createdAt}-${index}`} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '12px 14px', backgroundColor: '#fafafa' }}>
                        <div style={{ fontSize: '0.78rem', color: '#777', marginBottom: '8px' }}>
                          {new Date(note.createdAt).toLocaleString()} • {note.createdByEmail}
                        </div>
                        <div style={{ color: '#444', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{note.content}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {canViewProfileRecords && (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: 'var(--shadow)', padding: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', gap: '12px' }}>
                  <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                    <File size={22} /> Profile Files
                  </h3>
                  {canManageProfileRecords && (
                    <label htmlFor="profile-file-upload" className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <Upload size={18} />
                      {uploadingFile ? 'Uploading...' : 'Upload File'}
                      <input id="profile-file-upload" name="profileFileUpload" type="file" style={{ display: 'none' }} onChange={handleFileUpload} disabled={uploadingFile} />
                    </label>
                  )}
                </div>

                {files.length === 0 ? (
                  <div style={{ color: '#777', fontSize: '0.95rem' }}>No files available for this profile.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {files.map((file) => (
                      <div key={file.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', border: '1px solid #eee', borderRadius: '8px' }}>
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{file.fileName}</div>
                          <div style={{ color: '#777', fontSize: '0.8rem' }}>
                            Uploaded by {file.uploadedByRole || 'staff'} on {new Date(file.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <a href={file.fileUrl} target="_blank" rel="noreferrer" className="btn" style={{ backgroundColor: '#f5f5f5', padding: '8px 10px' }}>
                          <Download size={18} color="var(--primary-color)" />
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {isOwnProfile && (
              <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: 'var(--shadow)', padding: '30px' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <KeyRound size={22} /> Password Reset
              </h3>
              <p style={{ color: '#666', marginBottom: '18px' }}>Send a password reset email to your signed-in email address.</p>
              {resetMessage && <div style={{ marginBottom: '16px', color: '#1565c0', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '8px', padding: '12px 14px' }}>{resetMessage}</div>}
              <button type="button" className="btn btn-secondary" onClick={handlePasswordReset}>
                Send Password Reset Email
              </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;